
"use client";
import { API_ENDPOINTS } from "../../../constants/config";
import { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  File,
  X,
  Search,
  RefreshCw,
  Calendar,
  Clock,
  User,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { format, differenceInDays } from "date-fns";
import toast from "react-hot-toast";
import Pagination from "../../Common/Pagination";

export function LeaveApplicationList() {
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [remarks, setRemarks] = useState("");
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  
  // Server-side pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchLeaveApplications();
  }, [currentPage, itemsPerPage]);

  const fetchLeaveApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        API_ENDPOINTS.leaveApplicationsTeacher,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { paginate: "true", page: currentPage, limit: itemsPerPage },
        }
      );

      if (response.data && response.data.data) {
        setLeaveApplications(response.data.data);
        setTotalItems(response.data.pagination?.total ?? response.data.data.length);
      } else {
        setLeaveApplications([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching leave applications:", error);
      setError("Failed to fetch leave applications");
      setLeaveApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStudentName = (student) => {
    if (!student) return "N/A";

    const firstName = student.firstName || "";
    const middleName = student.middleName || "";
    const lastName = student.lastName || "";

    return [firstName, middleName, lastName].filter(Boolean).join(" ");
  };

  const getDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";
    try {
      const days = differenceInDays(new Date(endDate), new Date(startDate)) + 1;
      return `${days} ${days === 1 ? "day" : "days"}`;
    } catch (error) {
      return "N/A";
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      case "pending":
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const handleView = (application) => {
    setSelectedApplication(application);
    setRemarks("");
    setViewDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchLeaveApplications();
    toast.success("Leave applications refreshed");
  };

  const handleApprove = async () => {
    if (!selectedApplication) return;

    try {
      setProcessingAction(true);
      const token = localStorage.getItem("token");
      
      const response = await axiosInstance.patch(
        API_ENDPOINTS.leaveApplicationApprove(selectedApplication._id),
        { remarks },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Leave application approved successfully");
        setViewDialogOpen(false);
        fetchLeaveApplications();
      }
    } catch (error) {
      console.error("Error approving leave application:", error);
      toast.error(
        error.response?.data?.message || "Failed to approve leave application"
      );
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApplication) return;

    // Confirm rejection
    if (!window.confirm("Are you sure you want to reject this leave application?")) {
      return;
    }

    try {
      setProcessingAction(true);
      const token = localStorage.getItem("token");
      
      const response = await axiosInstance.patch(
        API_ENDPOINTS.leaveApplicationReject(selectedApplication._id),
        { remarks },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Leave application rejected successfully");
        setViewDialogOpen(false);
        fetchLeaveApplications();
      }
    } catch (error) {
      console.error("Error rejecting leave application:", error);
      toast.error(
        error.response?.data?.message || "Failed to reject leave application"
      );
    } finally {
      setProcessingAction(false);
    }
  };

  // Client-side filter/sort on current page data (server already paginates)
  const filteredApplications = leaveApplications.filter((app) => {
    const matchesSearch =
      !searchQuery ||
      getStudentName(app.studentId).toLowerCase().includes(searchQuery.toLowerCase()) ||
      (app.reason || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (app.studentId?.admissionNumber || "").toString().includes(searchQuery);

    const matchesStatus =
      statusFilter === "all" ||
      (app.status || "pending").toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const sortedApplications = [...filteredApplications].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id);
      case "oldest":
        return new Date(a.createdAt || a._id) - new Date(b.createdAt || b._id);
      case "name":
        return getStudentName(a.studentId).localeCompare(getStudentName(b.studentId));
      case "startDate":
        return new Date(a.startDate) - new Date(b.startDate);
      default:
        return 0;
    }
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, sortBy]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Leave Applications
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Section */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by student name, admission number, or reason..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                  <SelectItem value="startDate">Start Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="text-sm text-blue-600 font-medium">Total</div>
                <div className="text-2xl font-bold text-blue-800">
                  {totalItems}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4">
                <div className="text-sm text-yellow-600 font-medium">Pending</div>
                <div className="text-2xl font-bold text-yellow-800">
                  {
                    leaveApplications.filter(
                      (app) => (app.status || "pending").toLowerCase() === "pending"
                    ).length
                  }
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="text-sm text-green-600 font-medium">Approved</div>
                <div className="text-2xl font-bold text-green-800">
                  {
                    leaveApplications.filter(
                      (app) => app.status?.toLowerCase() === "approved"
                    ).length
                  }
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="text-sm text-red-600 font-medium">Rejected</div>
                <div className="text-2xl font-bold text-red-800">
                  {
                    leaveApplications.filter(
                      (app) => app.status?.toLowerCase() === "rejected"
                    ).length
                  }
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Table Section */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-2">{error}</div>
              <Button variant="outline" onClick={handleRefresh}>
                Try Again
              </Button>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {searchQuery || statusFilter !== "all"
                  ? "No leave applications match your filters"
                  : "No leave applications found"}
              </p>
              {(searchQuery || statusFilter !== "all") && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setSearchQuery("");
                    setStatusFilter("all");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Student Name</TableHead>
                      <TableHead className="font-semibold">Start Date</TableHead>
                      <TableHead className="font-semibold">End Date</TableHead>
                      <TableHead className="font-semibold">Duration</TableHead>
                      <TableHead className="font-semibold">Reason</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedApplications.map((application) => (
                      <TableRow key={application._id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            {getStudentName(application.studentId)}
                          </div>
                          {application.studentId?.admissionNumber && (
                            <div className="text-xs text-gray-500 mt-1">
                              Adm: {application.studentId.admissionNumber}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {formatDate(application.startDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {formatDate(application.endDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            {getDuration(
                              application.startDate,
                              application.endDate
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <div className="truncate" title={application.reason}>
                            {application.reason || "N/A"}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(application.status)}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(application)}
                            className="flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setCurrentPage(1); }}
                itemLabel="applications"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* View Application Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Leave Application Details
            </DialogTitle>
            <DialogDescription>
              Review the complete leave application details below.
            </DialogDescription>
          </DialogHeader>

          {selectedApplication && (
            <div className="space-y-6">
              {/* Status Badge */}
              <div className="flex justify-center">
                {getStatusBadge(selectedApplication.status)}
              </div>

              {/* Student Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Student Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-600">
                        Student Name
                      </div>
                      <div className="text-base font-semibold mt-1">
                        {getStudentName(selectedApplication.studentId)}
                      </div>
                    </div>
                    {selectedApplication.studentId?.admissionNumber && (
                      <div>
                        <div className="text-sm font-medium text-gray-600">
                          Admission Number
                        </div>
                        <div className="text-base font-semibold mt-1">
                          {selectedApplication.studentId.admissionNumber}
                        </div>
                      </div>
                    )}
                    {selectedApplication.studentId?.email && (
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-gray-600">
                          Email
                        </div>
                        <div className="text-base mt-1">
                          {selectedApplication.studentId.email}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Leave Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Leave Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-600">
                        Start Date
                      </div>
                      <div className="text-base font-semibold mt-1 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatDate(selectedApplication.startDate)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-600">
                        End Date
                      </div>
                      <div className="text-base font-semibold mt-1 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatDate(selectedApplication.endDate)}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-medium text-gray-600">
                        Duration
                      </div>
                      <div className="text-base font-semibold mt-1 flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        {getDuration(
                          selectedApplication.startDate,
                          selectedApplication.endDate
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-medium text-gray-600">
                        Reason
                      </div>
                      <div className="text-base mt-1 p-3 bg-gray-50 rounded-md">
                        {selectedApplication.reason || "N/A"}
                      </div>
                    </div>
                    {selectedApplication.createdAt && (
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-gray-600">
                          Applied On
                        </div>
                        <div className="text-base mt-1">
                          {formatDate(selectedApplication.createdAt)}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Attachments Section */}
              {selectedApplication.attachments &&
              selectedApplication.attachments.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <File className="h-5 w-5" />
                      Attachments ({selectedApplication.attachments.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-2">
                      {selectedApplication.attachments.map(
                        (attachment, index) => (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <File className="h-5 w-5 mr-3 text-blue-600" />
                            <span className="text-sm font-medium text-blue-600 hover:underline">
                              Attachment {index + 1}
                            </span>
                          </a>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <File className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <div className="text-sm text-gray-500">
                      No attachments provided
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Remarks Section - Show if pending */}
              {(selectedApplication.status === "pending" ||
                !selectedApplication.status) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Remarks (Optional)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Add any remarks or notes..."
                      className="w-full p-3 border rounded-md resize-none min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={4}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Show remarks if already processed */}
              {selectedApplication.remarks &&
                selectedApplication.status !== "pending" && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Remarks</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="p-3 bg-gray-50 rounded-md">
                        {selectedApplication.remarks}
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Show approved/rejected info */}
              {selectedApplication.approvedBy &&
                selectedApplication.status !== "pending" && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-gray-600">
                        {selectedApplication.status === "approved"
                          ? "Approved"
                          : "Rejected"}{" "}
                        by {selectedApplication.approvedBy?.firstName}{" "}
                        {selectedApplication.approvedBy?.lastName}
                        {selectedApplication.approvedAt && (
                          <span className="ml-2">
                            on {formatDate(selectedApplication.approvedAt)}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1"></div>
            <div className="flex gap-2">
              {(selectedApplication?.status === "pending" ||
                !selectedApplication?.status) && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleReject}
                    disabled={processingAction}
                    className="bg-red-50 text-red-700 hover:bg-red-100 border-red-200"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {processingAction ? "Rejecting..." : "Reject"}
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={processingAction}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {processingAction ? "Approving..." : "Approve"}
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setViewDialogOpen(false);
                  setRemarks("");
                }}
                disabled={processingAction}
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
